import type { ButtonHTMLAttributes } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import './IconButton.css';

export type IconButtonVariant = 'plain' | 'tonal' | 'float' | 'accent' | 'outline';

/** Class names for an IconButton look (links styled as icon buttons). */
export function iconButtonClass(variant: IconButtonVariant = 'plain', active = false, extra = '') {
  return [
    'icon-button',
    variant !== 'plain' && `icon-button--${variant}`,
    active && 'is-active',
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * DESIGN.md §10 IconButton: 44 × 44 hit area, 20 px icon, always labelled. `active` shows the
 * accent colour (favourite on); pass `aria-pressed` for toggles.
 */
export function IconButton({
  label,
  icon,
  variant = 'plain',
  active = false,
  filled = false,
  iconSize = 'm',
  className = '',
  type = 'button',
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'children'> & {
  label: string;
  icon: IconName;
  variant?: IconButtonVariant;
  active?: boolean;
  filled?: boolean;
  iconSize?: 's' | 'm' | 'l';
}) {
  return (
    <button
      type={type}
      aria-label={label}
      className={iconButtonClass(variant, active, className)}
      {...rest}
    >
      <Icon name={icon} size={iconSize} filled={filled} />
    </button>
  );
}
